import { Spinner, Button, Popover } from "@wordpress/components";
import { useState, useEffect, useRef, Fragment } from '@wordpress/element';
import { __ } from "@wordpress/i18n";
import { URLInput } from "@wordpress/block-editor";
import apiFetch from '@wordpress/api-fetch';
import { addQueryArgs } from '@wordpress/url';
import useDebounce from './use-debounce';

const LinkSelector = ({ link_id, onLinkLoaded, onChange, link_text, placement }) => {
  const [search, setSearch] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [selectedLink, setSelectedLink] = useState(null);
  const [isLoading, setIsLoading] = useState(link_id !== undefined && link_id !== "");
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(link_id === undefined || link_id === "");
  const debouncedSearch = useDebounce(search, 600);

  // Create a ref to store cached link data
  const linkCache = useRef({});

  useEffect(() => {
    if (link_id !== undefined && link_id !== "") {
      // Check if the link is already in the cache
      if (linkCache.current[link_id]) {
        const cachedLink = linkCache.current[link_id];
        setSelectedLink(cachedLink);
        setIsLoading(false);
        if (onLinkLoaded) {
          onLinkLoaded(cachedLink);
        }
      } else {
        getLink(link_id).then(link => {
          linkCache.current[link_id] = link; // Store the link in the cache
          setSelectedLink(link);
          setIsLoading(false);
          if (onLinkLoaded) {
            onLinkLoaded(link);
          }
        });
      }
    }
  }, [link_id]);

  useEffect(() => {
    if (debouncedSearch) {
      setIsSearchLoading(true);
      performSearch(debouncedSearch).then(searchResults => {
        setSuggestions(searchResults);
        setIsSearchLoading(false);
      });
    } else {
      setSuggestions([]);
    }
  }, [debouncedSearch]);

  const handleInputChange = (newText) => {
    setSearch(newText);
  };

  const handleSelectSuggestion = (selectedLink) => {
    setSelectedLink(selectedLink);
    setIsEditing(false);
    setIsLoading(false);
    onChange(selectedLink.id, selectedLink);
  };

  const handleBreak = () => {
    setIsLoading(true);
    getLinkByUrl(search).then(link => {
      if (link !== null) {
        handleSelectSuggestion(link);
      } else {
        setIsLoading(false);
      }
    });
  };

  const highlightQuery = (text, query) => {
    const regex = new RegExp(`(${query})`, 'gi');
    return text.replace(regex, '<mark>$1</mark>');
  };

  return (
    <div >
      <div className="linkgenius-preview editor-format-toolbar__link-container-content block-editor-format-toolbar__link-container-content">
        {selectedLink !== null ? (
          <a href={selectedLink.url} target="_blank">{link_text ?? selectedLink.url}</a>
        ) : (link_id === undefined && __('No link selected', 'linkgenius'))}
        {isLoading && (<Spinner />)}
        <Button icon="edit" label={__('Edit', 'linkgenius')} onClick={() => setIsEditing(true)} />
      </div>

      {isEditing && (
        <Popover
          focusOnMount={false}
          autoFocus={false}
          onClose={() => setIsEditing(false)}
          placement={placement ?? "bottom-start"}
          className="linkgenius-link-selector"
        >
          <div onKeyDown={(ev) => { if (ev.key === 'Enter') { handleBreak() } }} className="link-input">
            <URLInput value={search} onChange={handleInputChange} disableSuggestions={true} autoFocus={true} />
            {isSearchLoading && <Spinner className="search-spinner" />}
            <Button icon="editor-break" label={__('Insert LinkGenius Link')} onClick={handleBreak} />
          </div>
          {suggestions.length > 0 && (
            <Fragment>
              <div role="listbox" className="link-list">
                {suggestions.map(link => (
                  <button className="link-button" key={link.id} onClick={() => handleSelectSuggestion(link)}>
                    <div className="link-title" dangerouslySetInnerHTML={{ __html: highlightQuery(link.title, search) }}></div>
                    <div className="link-url" dangerouslySetInnerHTML={{ __html: highlightQuery(link.url, search) }}></div>
                    <div className="link-target" dangerouslySetInnerHTML={{ __html: highlightQuery(link.target_url, search) }}></div>
                  </button>
                ))}
              </div>
            </Fragment>
          )}
        </Popover>
      )}
    </div>
  );
};

async function performSearch(query) {
  const response = await apiFetch({
    url: addQueryArgs(ajaxurl, {
      action: 'search_linkgenius_links',
      keyword: query
    })
  });
  if (response.success) {
    return response.data;
  } else {
    return [];
  }
}

const linkgeniusLinkCache = {};
async function getLink(id) {
  if (linkgeniusLinkCache[id]) {
    return linkgeniusLinkCache[id];
  }
  const response = await apiFetch({
    url: addQueryArgs(ajaxurl, {
      action: 'get_linkgenius_link',
      linkgenius_id: id
    })
  });
  if (response.success) {
    linkgeniusLinkCache[id] = response.data;
    return response.data;
  } else {
    return null;
  }
}

async function getLinkByUrl(url) {
  const response = await apiFetch({
    url: addQueryArgs(ajaxurl, {
      action: 'get_linkgenius_link',
      linkgenius_url: url
    })
  });
  if (response.success) {
    return response.data;
  } else {
    return null;
  }
}

export { LinkSelector, getLink };